/**
 * Universal Logger Filter Configuration System
 * 
 * Flexible configuration system for managing filtering rules, patterns, and policies
 * with runtime updates, validation, and hot-reload capabilities.
 */

import { 
  FilterRule, 
  FilterRuleSet, 
  FilterEngineConfig,
  SensitiveDataCategory,
  FilterAction,
  FilterPriority,
  FilterSeverity,
  FilterRuleType 
} from './filter-types';
import { getAllPredefinedPatternsFlat, getAllPredefinedPatterns } from './filter-patterns';

// Configuration Schema Types
export interface FilterConfigurationSchema {
  version: string;
  metadata: FilterConfigMetadata;
  globalSettings: GlobalFilterSettings;
  categories: CategoryConfiguration[];
  ruleSets: RuleSetConfiguration[];
  rules: FilterRule[];
  validation: ValidationSettings;
  monitoring: MonitoringSettings;
}

export interface FilterConfigMetadata {
  name: string;
  description: string;
  version: string;
  author: string;
  createdAt: string;
  updatedAt: string;
  environment: 'development' | 'staging' | 'production';
  tags: string[];
}

export interface GlobalFilterSettings {
  enabled: boolean;
  defaultAction: FilterAction;
  defaultPriority: FilterPriority;
  defaultSeverity: FilterSeverity;
  cacheEnabled: boolean;
  cacheTtl: number;
  cacheSize: number;
  performanceMode: 'fast' | 'balanced' | 'thorough';
  failureMode: 'strict' | 'permissive' | 'fallback';
  enableAuditLogging: boolean;
  maxAuditEntries: number;
}

export interface CategoryConfiguration {
  category: SensitiveDataCategory;
  enabled: boolean;
  priority: FilterPriority;
  defaultAction: FilterAction;
  ruleCount: number;
  description: string;
  complianceStandards: string[];
}

export interface RuleSetConfiguration {
  id: string;
  name: string;
  description: string;
  category: SensitiveDataCategory;
  enabled: boolean;
  priority: FilterPriority;
  ruleIds: string[];
  tags: string[];
  version: string;
  createdAt: string;
  updatedAt: string;
}

export interface ValidationSettings {
  strictMode: boolean;
  validatePatterns: boolean;
  validateRegexSafety: boolean;
  maxPatternLength: number;
  maxRulesetSize: number;
  allowCustomPatterns: boolean;
  requiredFields: string[];
}

export interface MonitoringSettings {
  enableMetrics: boolean;
  metricsInterval: number;
  enablePerformanceTracking: boolean;
  enableHealthChecks: boolean;
  healthCheckInterval: number;
  alertThresholds: AlertThresholds;
}

export interface AlertThresholds {
  filterLatency: number; // ms
  errorRate: number; // percentage
  memoryUsage: number; // MB
  cacheHitRate: number; // percentage
}

// Rule Management APIs
export interface RuleManager {
  loadConfiguration(config: FilterConfigurationSchema): Promise<void>;
  saveConfiguration(): Promise<FilterConfigurationSchema>;
  validateConfiguration(config: FilterConfigurationSchema): ValidationResult;
  addRule(rule: FilterRule): Promise<void>;
  updateRule(ruleId: string, updates: Partial<FilterRule>): Promise<void>;
  removeRule(ruleId: string): Promise<void>;
  addRuleSet(ruleSet: FilterRuleSet): Promise<void>;
  updateRuleSet(ruleSetId: string, updates: Partial<FilterRuleSet>): Promise<void>;
  removeRuleSet(ruleSetId: string): Promise<void>;
  enableRule(ruleId: string): Promise<void>;
  disableRule(ruleId: string): Promise<void>;
  enableRuleSet(ruleSetId: string): Promise<void>;
  disableRuleSet(ruleSetId: string): Promise<void>;
  reloadConfiguration(): Promise<void>;
  exportConfiguration(): Promise<FilterConfigurationSchema>;
  importConfiguration(config: FilterConfigurationSchema): Promise<void>;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  summary: ValidationSummary;
}

export interface ValidationError {
  field: string;
  message: string;
  code: string;
  severity: 'error' | 'warning';
  suggestion?: string;
}

export interface ValidationWarning {
  field: string;
  message: string;
  code: string;
  recommendation: string;
}

export interface ValidationSummary {
  totalRules: number;
  validRules: number;
  invalidRules: number;
  totalRuleSets: number;
  validRuleSets: number;
  invalidRuleSets: number;
  configVersion: string;
  validationTime: number;
}

// Configuration Manager Implementation
export class FilterConfigurationManager implements RuleManager {
  private config: FilterConfigurationSchema;
  private rules = new Map<string, FilterRule>();
  private ruleSets = new Map<string, FilterRuleSet>();
  private watchers: Array<(config: FilterConfigurationSchema) => void> = [];
  private validationCache = new Map<string, ValidationResult>();
  private lastReload = Date.now();

  constructor(config?: Partial<FilterConfigurationSchema>) {
    this.config = this.mergeWithDefaults(config);
    this.loadRulesFromConfig();
  }

  private mergeWithDefaults(config?: Partial<FilterConfigurationSchema>): FilterConfigurationSchema {
    const defaults: FilterConfigurationSchema = {
      version: '1.0.0',
      metadata: {
        name: 'Universal Logger Filter Configuration',
        description: 'Default filter configuration for Universal Logger',
        version: '1.0.0',
        author: 'Universal Logger System',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        environment: 'development',
        tags: ['default', 'universal-logger']
      },
      globalSettings: {
        enabled: true,
        defaultAction: FilterAction.REDACT,
        defaultPriority: FilterPriority.MEDIUM,
        defaultSeverity: FilterSeverity.MEDIUM,
        cacheEnabled: true,
        cacheTtl: 300000, // 5 minutes
        cacheSize: 1000,
        performanceMode: 'balanced',
        failureMode: 'permissive',
        enableAuditLogging: true,
        maxAuditEntries: 10000
      },
      categories: this.generateDefaultCategories(),
      ruleSets: [],
      rules: [],
      validation: {
        strictMode: false,
        validatePatterns: true,
        validateRegexSafety: true,
        maxPatternLength: 10000,
        maxRulesetSize: 1000,
        allowCustomPatterns: true,
        requiredFields: ['id', 'name', 'category', 'type', 'pattern', 'action']
      },
      monitoring: {
        enableMetrics: true,
        metricsInterval: 60000, // 1 minute
        enablePerformanceTracking: true,
        enableHealthChecks: true,
        healthCheckInterval: 30000, // 30 seconds
        alertThresholds: {
          filterLatency: 10, // 10ms
          errorRate: 5, // 5%
          memoryUsage: 100, // 100MB
          cacheHitRate: 80 // 80%
        }
      }
    };

    return { ...defaults, ...config } as FilterConfigurationSchema;
  }

  private generateDefaultCategories(): CategoryConfiguration[] {
    return [
      {
        category: SensitiveDataCategory.PII,
        enabled: true,
        priority: FilterPriority.HIGH,
        defaultAction: FilterAction.REDACT,
        ruleCount: 0,
        description: 'Personally Identifiable Information',
        complianceStandards: ['GDPR', 'CCPA', 'PIPEDA']
      },
      {
        category: SensitiveDataCategory.AUTHENTICATION,
        enabled: true,
        priority: FilterPriority.HIGHEST,
        defaultAction: FilterAction.REMOVE,
        ruleCount: 0,
        description: 'Authentication credentials and tokens',
        complianceStandards: ['SOC2', 'ISO27001', 'NIST']
      },
      {
        category: SensitiveDataCategory.FINANCIAL,
        enabled: true,
        priority: FilterPriority.HIGH,
        defaultAction: FilterAction.REDACT,
        ruleCount: 0,
        description: 'Financial and payment information',
        complianceStandards: ['PCI-DSS', 'SOX', 'GLBA']
      },
      {
        category: SensitiveDataCategory.MEDICAL,
        enabled: true,
        priority: FilterPriority.HIGH,
        defaultAction: FilterAction.REDACT,
        ruleCount: 0,
        description: 'Medical and health information',
        complianceStandards: ['HIPAA', 'HITECH', 'FDA']
      },
      {
        category: SensitiveDataCategory.CUSTOM,
        enabled: true,
        priority: FilterPriority.MEDIUM,
        defaultAction: FilterAction.MASK,
        ruleCount: 0,
        description: 'Custom organization-specific sensitive data',
        complianceStandards: ['Custom']
      }
    ];
  }

  private loadRulesFromConfig(): void {
    // Load predefined patterns as rules
    const predefinedPatterns = getAllPredefinedPatternsFlat();
    predefinedPatterns.forEach(pattern => {
      const rule: FilterRule = {
        id: pattern.id,
        name: pattern.name,
        description: pattern.description,
        category: pattern.category,
        type: FilterRuleType.REGEX,
        pattern: pattern.regex,
        action: this.getCategoryConfig(pattern.category)?.defaultAction || FilterAction.REDACT,
        priority: this.getCategoryConfig(pattern.category)?.priority || FilterPriority.MEDIUM,
        severity: FilterSeverity.MEDIUM,
        enabled: true,
        tags: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      this.rules.set(rule.id, rule);
    });

    // Load custom rules from config
    this.config.rules.forEach(rule => {
      this.rules.set(rule.id, rule);
    });

    // Update category rule counts
    this.updateCategoryRuleCounts();
  }

  private getCategoryConfig(category: SensitiveDataCategory): CategoryConfiguration | undefined {
    return this.config.categories.find(cat => cat.category === category);
  }

  private updateCategoryRuleCounts(): void {
    this.config.categories.forEach(categoryConfig => {
      categoryConfig.ruleCount = Array.from(this.rules.values())
        .filter(rule => rule.category === categoryConfig.category && rule.enabled)
        .length;
    });
  }

  async loadConfiguration(config: FilterConfigurationSchema): Promise<void> {
    const validation = this.validateConfiguration(config);
    if (!validation.valid && this.config.validation.strictMode) {
      throw new Error(`Configuration validation failed: ${validation.errors.map(e => e.message).join(', ')}`);
    }

    this.config = config;
    this.rules.clear();
    this.ruleSets.clear();
    this.loadRulesFromConfig();
    this.lastReload = Date.now();
    this.notifyWatchers();
  }

  async saveConfiguration(): Promise<FilterConfigurationSchema> {
    this.config.metadata.updatedAt = new Date().toISOString();
    this.config.rules = Array.from(this.rules.values());
    return { ...this.config };
  }

  validateConfiguration(config: FilterConfigurationSchema): ValidationResult {
    const cacheKey = JSON.stringify(config);
    if (this.validationCache.has(cacheKey)) {
      return this.validationCache.get(cacheKey)!;
    }

    const startTime = Date.now();
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];

    // Validate metadata
    if (!config.metadata?.name || config.metadata.name.trim().length === 0) {
      errors.push({
        field: 'metadata.name',
        message: 'Configuration name is required',
        code: 'REQUIRED_FIELD',
        severity: 'error'
      });
    }

    // Validate global settings
    if (config.globalSettings?.cacheTtl && config.globalSettings.cacheTtl < 0) {
      errors.push({
        field: 'globalSettings.cacheTtl',
        message: 'Cache TTL must be non-negative',
        code: 'INVALID_VALUE',
        severity: 'error'
      });
    }

    // Validate rules
    let validRules = 0;
    let invalidRules = 0;

    config.rules?.forEach((rule, index) => {
      const ruleErrors = this.validateRule(rule, `rules[${index}]`);
      if (ruleErrors.length === 0) {
        validRules++;
      } else {
        invalidRules++;
        errors.push(...ruleErrors);
      }
    });

    // Validate rule sets
    let validRuleSets = 0;
    let invalidRuleSets = 0;

    config.ruleSets?.forEach((ruleSet, index) => {
      const ruleSetErrors = this.validateRuleSet(ruleSet, `ruleSets[${index}]`);
      if (ruleSetErrors.length === 0) {
        validRuleSets++;
      } else {
        invalidRuleSets++;
        errors.push(...ruleSetErrors);
      }
    });

    // Performance warnings
    if (config.rules && config.rules.length > 1000) {
      warnings.push({
        field: 'rules',
        message: 'Large number of rules may impact performance',
        code: 'PERFORMANCE_WARNING',
        recommendation: 'Consider grouping rules into rule sets for better organization'
      });
    }

    const result: ValidationResult = {
      valid: errors.length === 0,
      errors,
      warnings,
      summary: {
        totalRules: config.rules?.length || 0,
        validRules,
        invalidRules,
        totalRuleSets: config.ruleSets?.length || 0,
        validRuleSets,
        invalidRuleSets,
        configVersion: config.version,
        validationTime: Date.now() - startTime
      }
    };

    this.validationCache.set(cacheKey, result);
    return result;
  }

  private validateRule(rule: FilterRule, fieldPrefix: string): ValidationError[] {
    const errors: ValidationError[] = [];

    // Required fields validation
    this.config.validation.requiredFields.forEach(field => {
      if (!(rule as any)[field]) {
        errors.push({
          field: `${fieldPrefix}.${field}`,
          message: `Field '${field}' is required`,
          code: 'REQUIRED_FIELD',
          severity: 'error'
        });
      }
    });

    // Pattern validation
    if (this.config.validation.validatePatterns && rule.pattern) {
      try {
        if (typeof rule.pattern === 'string') {
          // Test if string can be used as regex
          new RegExp(rule.pattern);
        }
        
        if (this.config.validation.validateRegexSafety) {
          const patternStr = rule.pattern.toString();
          if (patternStr.length > this.config.validation.maxPatternLength) {
            errors.push({
              field: `${fieldPrefix}.pattern`,
              message: `Pattern length exceeds maximum allowed (${this.config.validation.maxPatternLength})`,
              code: 'PATTERN_TOO_LONG',
              severity: 'error'
            });
          }

          // Check for potentially dangerous regex patterns
          if (this.isDangerousPattern(patternStr)) {
            errors.push({
              field: `${fieldPrefix}.pattern`,
              message: 'Pattern may cause ReDoS (Regular Expression Denial of Service)',
              code: 'UNSAFE_PATTERN',
              severity: 'error',
              suggestion: 'Simplify the pattern or use alternative matching approaches'
            });
          }
        }
      } catch (error) {
        errors.push({
          field: `${fieldPrefix}.pattern`,
          message: `Invalid regex pattern: ${(error as Error).message}`,
          code: 'INVALID_PATTERN',
          severity: 'error'
        });
      }
    }

    return errors;
  }

  private validateRuleSet(ruleSet: FilterRuleSet, fieldPrefix: string): ValidationError[] {
    const errors: ValidationError[] = [];

    if (!ruleSet.id || ruleSet.id.trim().length === 0) {
      errors.push({
        field: `${fieldPrefix}.id`,
        message: 'RuleSet ID is required',
        code: 'REQUIRED_FIELD',
        severity: 'error'
      });
    }

    if (!ruleSet.name || ruleSet.name.trim().length === 0) {
      errors.push({
        field: `${fieldPrefix}.name`,
        message: 'RuleSet name is required',
        code: 'REQUIRED_FIELD',
        severity: 'error'
      });
    }

    if (ruleSet.rules && ruleSet.rules.length > this.config.validation.maxRulesetSize) {
      errors.push({
        field: `${fieldPrefix}.rules`,
        message: `RuleSet contains too many rules (max: ${this.config.validation.maxRulesetSize})`,
        code: 'RULESET_TOO_LARGE',
        severity: 'error'
      });
    }

    return errors;
  }

  private isDangerousPattern(pattern: string): boolean {
    // Check for common ReDoS patterns
    const dangerousPatterns = [
      /\(\.\*\)\+/, // (.*)+
      /\(\.\+\)\+/, // (.+)+
      /\(\w+\)\+\w*\\\1/, // backreference after + quantifier
      /\(\w*\)\*\w*\\\1/, // backreference after * quantifier
    ];

    return dangerousPatterns.some(dangerous => dangerous.test(pattern));
  }

  async addRule(rule: FilterRule): Promise<void> {
    const validation = this.validateRule(rule, 'rule');
    if (validation.length > 0 && this.config.validation.strictMode) {
      throw new Error(`Rule validation failed: ${validation.map(e => e.message).join(', ')}`);
    }

    this.rules.set(rule.id, rule);
    this.config.rules = Array.from(this.rules.values());
    this.updateCategoryRuleCounts();
    this.config.metadata.updatedAt = new Date().toISOString();
    this.clearValidationCache();
    this.notifyWatchers();
  }

  async updateRule(ruleId: string, updates: Partial<FilterRule>): Promise<void> {
    const existingRule = this.rules.get(ruleId);
    if (!existingRule) {
      throw new Error(`Rule with ID '${ruleId}' not found`);
    }

    const updatedRule = { ...existingRule, ...updates, updatedAt: new Date().toISOString() };
    
    const validation = this.validateRule(updatedRule, 'rule');
    if (validation.length > 0 && this.config.validation.strictMode) {
      throw new Error(`Rule validation failed: ${validation.map(e => e.message).join(', ')}`);
    }

    this.rules.set(ruleId, updatedRule);
    this.config.rules = Array.from(this.rules.values());
    this.updateCategoryRuleCounts();
    this.config.metadata.updatedAt = new Date().toISOString();
    this.clearValidationCache();
    this.notifyWatchers();
  }

  async removeRule(ruleId: string): Promise<void> {
    if (!this.rules.has(ruleId)) {
      throw new Error(`Rule with ID '${ruleId}' not found`);
    }

    this.rules.delete(ruleId);
    this.config.rules = Array.from(this.rules.values());
    this.updateCategoryRuleCounts();
    this.config.metadata.updatedAt = new Date().toISOString();
    this.clearValidationCache();
    this.notifyWatchers();
  }

  async addRuleSet(ruleSet: FilterRuleSet): Promise<void> {
    const validation = this.validateRuleSet(ruleSet, 'ruleSet');
    if (validation.length > 0 && this.config.validation.strictMode) {
      throw new Error(`RuleSet validation failed: ${validation.map(e => e.message).join(', ')}`);
    }

    this.ruleSets.set(ruleSet.id, ruleSet);
    this.config.metadata.updatedAt = new Date().toISOString();
    this.clearValidationCache();
    this.notifyWatchers();
  }

  async updateRuleSet(ruleSetId: string, updates: Partial<FilterRuleSet>): Promise<void> {
    const existingRuleSet = this.ruleSets.get(ruleSetId);
    if (!existingRuleSet) {
      throw new Error(`RuleSet with ID '${ruleSetId}' not found`);
    }

    const updatedRuleSet = { ...existingRuleSet, ...updates, updatedAt: new Date().toISOString() };
    
    const validation = this.validateRuleSet(updatedRuleSet, 'ruleSet');
    if (validation.length > 0 && this.config.validation.strictMode) {
      throw new Error(`RuleSet validation failed: ${validation.map(e => e.message).join(', ')}`);
    }

    this.ruleSets.set(ruleSetId, updatedRuleSet);
    this.config.metadata.updatedAt = new Date().toISOString();
    this.clearValidationCache();
    this.notifyWatchers();
  }

  async removeRuleSet(ruleSetId: string): Promise<void> {
    if (!this.ruleSets.has(ruleSetId)) {
      throw new Error(`RuleSet with ID '${ruleSetId}' not found`);
    }

    this.ruleSets.delete(ruleSetId);
    this.config.metadata.updatedAt = new Date().toISOString();
    this.clearValidationCache();
    this.notifyWatchers();
  }

  async enableRule(ruleId: string): Promise<void> {
    await this.updateRule(ruleId, { enabled: true });
  }

  async disableRule(ruleId: string): Promise<void> {
    await this.updateRule(ruleId, { enabled: false });
  }

  async enableRuleSet(ruleSetId: string): Promise<void> {
    await this.updateRuleSet(ruleSetId, { enabled: true });
  }

  async disableRuleSet(ruleSetId: string): Promise<void> {
    await this.updateRuleSet(ruleSetId, { enabled: false });
  }

  async reloadConfiguration(): Promise<void> {
    // In a real implementation, this would reload from file system or remote source
    this.lastReload = Date.now();
    this.clearValidationCache();
    this.notifyWatchers();
  }

  async exportConfiguration(): Promise<FilterConfigurationSchema> {
    return this.saveConfiguration();
  }

  async importConfiguration(config: FilterConfigurationSchema): Promise<void> {
    await this.loadConfiguration(config);
  }

  // Configuration watching and hot-reload
  addConfigurationWatcher(callback: (config: FilterConfigurationSchema) => void): void {
    this.watchers.push(callback);
  }

  removeConfigurationWatcher(callback: (config: FilterConfigurationSchema) => void): void {
    const index = this.watchers.indexOf(callback);
    if (index !== -1) {
      this.watchers.splice(index, 1);
    }
  }

  private notifyWatchers(): void {
    this.watchers.forEach(watcher => {
      try {
        watcher(this.config);
      } catch (error) {
        console.error('Error in configuration watcher:', error);
      }
    });
  }

  private clearValidationCache(): void {
    this.validationCache.clear();
  }

  // Getters for accessing configuration
  getConfiguration(): FilterConfigurationSchema {
    return { ...this.config };
  }

  getGlobalSettings(): GlobalFilterSettings {
    return { ...this.config.globalSettings };
  }

  getCategoryConfigurations(): CategoryConfiguration[] {
    return [...this.config.categories];
  }

  getRules(): FilterRule[] {
    return Array.from(this.rules.values());
  }

  getRuleSets(): FilterRuleSet[] {
    return Array.from(this.ruleSets.values());
  }

  getRule(ruleId: string): FilterRule | undefined {
    return this.rules.get(ruleId);
  }

  getRuleSet(ruleSetId: string): FilterRuleSet | undefined {
    return this.ruleSets.get(ruleSetId);
  }

  // Statistics and monitoring
  getConfigurationStats(): {
    totalRules: number;
    enabledRules: number;
    disabledRules: number;
    totalRuleSets: number;
    enabledRuleSets: number;
    lastReload: number;
    categories: Record<SensitiveDataCategory, number>;
  } {
    const rules = Array.from(this.rules.values());
    const ruleSets = Array.from(this.ruleSets.values());
    
    const categories = {} as Record<SensitiveDataCategory, number>;
    Object.values(SensitiveDataCategory).forEach(category => {
      categories[category] = rules.filter(rule => rule.category === category && rule.enabled).length;
    });

    return {
      totalRules: rules.length,
      enabledRules: rules.filter(rule => rule.enabled).length,
      disabledRules: rules.filter(rule => !rule.enabled).length,
      totalRuleSets: ruleSets.length,
      enabledRuleSets: ruleSets.filter(ruleSet => ruleSet.enabled).length,
      lastReload: this.lastReload,
      categories
    };
  }
}

// Default rule sets for common use cases
export const DefaultRuleSets = {
  BASIC_PII: {
    id: 'basic-pii',
    name: 'Basic PII Protection',
    description: 'Essential personally identifiable information patterns',
    category: SensitiveDataCategory.PII,
    enabled: true,
    priority: FilterPriority.HIGH,
    rules: [], // Will be populated with relevant rule IDs
    tags: ['pii', 'basic', 'essential'],
    version: '1.0.0',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  SECURITY_CREDENTIALS: {
    id: 'security-credentials',
    name: 'Security Credentials Protection',
    description: 'API keys, tokens, and authentication credentials',
    category: SensitiveDataCategory.AUTHENTICATION,
    enabled: true,
    priority: FilterPriority.CRITICAL,
    rules: [], // Will be populated with relevant rule IDs
    tags: ['security', 'credentials', 'authentication'],
    version: '1.0.0',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  FINANCIAL_DATA: {
    id: 'financial-data',
    name: 'Financial Data Protection',
    description: 'Credit cards, bank accounts, and financial information',
    category: SensitiveDataCategory.FINANCIAL,
    enabled: true,
    priority: FilterPriority.HIGH,
    rules: [], // Will be populated with relevant rule IDs
    tags: ['financial', 'payment', 'banking'],
    version: '1.0.0',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
};

// Factory function for creating configured filter managers
export function createFilterConfigurationManager(config?: Partial<FilterConfigurationSchema>): FilterConfigurationManager {
  return new FilterConfigurationManager(config);
}

// Helper function to create production-ready configuration
export function createProductionConfiguration(): FilterConfigurationSchema {
  const manager = new FilterConfigurationManager({
    metadata: {
      environment: 'production',
      name: 'Production Filter Configuration',
      description: 'Production-optimized filter configuration with strict security'
    },
    globalSettings: {
      enabled: true,
      defaultAction: FilterAction.REMOVE,
      defaultPriority: FilterPriority.HIGH,
      defaultSeverity: FilterSeverity.HIGH,
      performanceMode: 'fast',
      failureMode: 'strict',
      enableAuditLogging: true
    },
    validation: {
      strictMode: true,
      validatePatterns: true,
      validateRegexSafety: true,
      maxPatternLength: 5000,
      maxRulesetSize: 500,
      allowCustomPatterns: false,
      requiredFields: ['id', 'name', 'category', 'type', 'pattern', 'action', 'priority']
    }
  });
  
  return manager.getConfiguration();
} 